import React, { useCallback, useEffect, useState } from 'react';
import { Car, Search, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/axios';

const CATEGORIAS = [['', 'Todas as categorias'], ['ex_tvde', 'Ex-TVDE'],
  ['oportunidade', 'Oportunidade para TVDE'], ['legacy_pending_classification', 'Classificação por completar']];
const VENDEDORES = [['', 'Todos os vendedores'], ['particular', 'Particular'], ['empresa', 'Empresa'],
  ['profissional', 'Profissional'], ['stand', 'Stand']];
const OPERACIONAIS = [['', 'Todos os estados'], ['operacional', 'Operacional'],
  ['operacional_anomalias', 'Operacional com anomalias'], ['avariada', 'Avariada'],
  ['imobilizada', 'Imobilizada'], ['pecas', 'Para peças']];
const EDITORIAIS = [['', 'Todos'], ['pendente', 'Pendente'], ['aprovado', 'Aprovado'],
  ['rejeitado', 'Rejeitado'], ['pausado', 'Pausado'], ['vendido', 'Vendido'],
  ['expirado', 'Expirado'], ['arquivado', 'Arquivado']];
const COMERCIAIS = [['', 'Todos'], ['isento_lancamento', 'Gratuito'], ['pendente', 'Pagamento pendente'],
  ['pago', 'Pago'], ['reembolsado', 'Reembolsado']];

const ACOES = [['aprovar', 'Aprovar'], ['rejeitar', 'Rejeitar'], ['pedir_correcao', 'Pedir correção'],
  ['pausar', 'Pausar'], ['marcar_vendido', 'Marcar vendido'], ['arquivar', 'Arquivar']];

export default function MarketplaceSalesAdminPanel({ initialFilters = {} }) {
  const [filters, setFilters] = useState({
    finalidade: 'venda', categoria: '', tipo_vendedor: '', estado_operacional: '',
    estado_editorial: '', estado_comercial: '', q: '', ...initialFilters,
  });
  const [data, setData] = useState({ items: [], total: 0 });
  const [open, setOpen] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [migracao, setMigracao] = useState(null);
  const [pending, setPending] = useState(null); // { id, acao } para motivo/categoria em linha
  const [pendingValue, setPendingValue] = useState('');

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const r = await api.get('/admin/marketplace/venda/publicacoes', { params: { ...params, limit: 30 } });
      setData(r.data);
      setErr('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha a carregar anúncios');
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const moderar = async (id, acao, valor = null) => {
    if ((acao === 'rejeitar' || acao === 'pedir_correcao' || acao === 'classificar') && valor === null) {
      setPending({ id, acao });
      setPendingValue('');
      return;
    }
    setBusy(id);
    try {
      const body = acao === 'classificar' ? { acao, categoria: valor } : { acao, motivo: valor };
      await api.post(`/admin/marketplace/venda/publicacoes/${id}/moderacao`, body);
      setMsg('Anúncio atualizado.');
      setPending(null);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha na ação');
    } finally { setBusy(''); }
  };

  const migrar = async (dryRun) => {
    try {
      const r = await api.post(`/admin/marketplace/venda/migrar?dry_run=${dryRun}`);
      setMigracao(r.data);
      if (!dryRun) await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha na migração');
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="marketplace-sales-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <Car className="h-5 w-5 text-amber-600" /> Compra e venda de viaturas
        </h2>
        <div className="flex gap-2">
          <button onClick={() => migrar(true)} className="rounded-lg border px-3 py-1.5 text-xs font-semibold" data-testid="sales-migrate-dry">Migração (simular)</button>
          <button onClick={() => migrar(false)} className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800" data-testid="sales-migrate-run">Migrar antigos</button>
        </div>
      </div>

      {migracao && (
        <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="sales-migration-result">
          {migracao.dry_run ? 'Simulação' : 'Migração'}: {migracao.candidatos} candidato(s), {migracao.migrados} migrado(s).
          Total de anúncios {migracao.total_anuncios_antes} → {migracao.total_anuncios_depois}.
          Sem classificação: {migracao.sem_classificacao}.
        </p>
      )}
      {msg && <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="sales-admin-msg">{msg}</p>}
      {err && <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="sales-admin-error">{err}</p>}

      <div className="mt-4 grid gap-2 md:grid-cols-4" data-testid="sales-admin-filters">
        <select value={filters.finalidade} onChange={(e) => setFilters((f) => ({ ...f, finalidade: e.target.value }))} className="rounded-lg border px-2 py-1.5 text-xs" data-testid="sales-filter-finalidade">
          <option value="">Aluguer e venda</option><option value="venda">Só venda</option><option value="aluguer">Só aluguer</option>
        </select>
        <select value={filters.categoria} onChange={(e) => setFilters((f) => ({ ...f, categoria: e.target.value }))} className="rounded-lg border px-2 py-1.5 text-xs" data-testid="sales-filter-categoria">
          {CATEGORIAS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.tipo_vendedor} onChange={(e) => setFilters((f) => ({ ...f, tipo_vendedor: e.target.value }))} className="rounded-lg border px-2 py-1.5 text-xs" data-testid="sales-filter-vendedor">
          {VENDEDORES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.estado_operacional} onChange={(e) => setFilters((f) => ({ ...f, estado_operacional: e.target.value }))} className="rounded-lg border px-2 py-1.5 text-xs" data-testid="sales-filter-operacional">
          {OPERACIONAIS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.estado_editorial} onChange={(e) => setFilters((f) => ({ ...f, estado_editorial: e.target.value }))} className="rounded-lg border px-2 py-1.5 text-xs" data-testid="sales-filter-editorial">
          {EDITORIAIS.map(([k, v]) => <option key={k} value={k}>Editorial: {v}</option>)}
        </select>
        <select value={filters.estado_comercial} onChange={(e) => setFilters((f) => ({ ...f, estado_comercial: e.target.value }))} className="rounded-lg border px-2 py-1.5 text-xs" data-testid="sales-filter-comercial">
          {COMERCIAIS.map(([k, v]) => <option key={k} value={k}>Pagamento: {v}</option>)}
        </select>
        <div className="relative md:col-span-2">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Marca, modelo, anunciante ou localização" className="w-full rounded-lg border py-1.5 pl-7 pr-2 text-xs" data-testid="sales-filter-search" />
        </div>
        <button onClick={load} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white md:col-span-4" data-testid="sales-apply-filters">
          <Filter className="mr-1 inline h-3 w-3" /> Aplicar filtros ({data.total})
        </button>
      </div>

      <div className="mt-4 space-y-2" data-testid="sales-admin-list">
        {data.items.length === 0 && <p className="text-xs text-slate-500" data-testid="sales-admin-empty">Nenhum anúncio com estes filtros.</p>}
        {data.items.map((it) => {
          const venda = it.venda || {};
          const v = venda.viatura || {};
          return (
            <div key={it.id} className="rounded-xl border border-slate-200 p-3" data-testid={`sales-admin-item-${it.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900">{it.titulo}</p>
                  <p className="text-[11px] text-slate-500">
                    {it.tipo === 'venda' ? 'Venda' : 'Aluguer'} · {venda.categoria || '—'} · {venda.tipo_vendedor || '—'} ·{' '}
                    {venda.concelho || it.cidade || '—'} · {it.status} · pagamento: {(it.payment || {}).status || '—'}
                  </p>
                </div>
                <button onClick={() => setOpen(open === it.id ? null : it.id)} className="rounded-lg border px-2 py-1 text-[11px]" data-testid={`sales-admin-toggle-${it.id}`}>
                  {open === it.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
              </div>

              {open === it.id && (
                <div className="mt-2 grid gap-3 text-[11px] text-slate-600 md:grid-cols-2" data-testid={`sales-admin-detail-${it.id}`}>
                  <div>
                    <p className="font-bold text-slate-800">Viatura declarada</p>
                    <p>{v.marca} {v.modelo} {v.versao || ''} · {v.ano} · {v.quilometros} km · {v.combustivel} · {v.caixa}</p>
                    <p>Origem: {v.origem}{v.pais_origem ? ` (${v.pais_origem})` : ''}</p>
                    <p>Preço: {(venda.comercial || {}).preco} €</p>
                    <p>Estado: {(venda.estado || {}).principal}</p>
                    <p>Nome público: {venda.nome_publico}</p>
                    <p>Email do anunciante: {(it.contacto || {}).email}</p>
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">Histórico administrativo</p>
                    <ul className="space-y-0.5">
                      {(it.historico || []).slice(-6).map((h, i) => (
                        <li key={i}>{(h.timestamp || '').slice(0, 16)} · {h.acao} · {h.admin_email || h.admin_nome || '—'} {h.nota ? `· ${h.nota}` : ''}</li>
                      ))}
                      {!(it.historico || []).length && <li>Sem ações registadas.</li>}
                    </ul>
                  </div>
                </div>
              )}

              <div className="mt-2 flex flex-wrap gap-1.5">
                {ACOES.map(([acao, label]) => (
                  <button key={acao} disabled={busy === it.id} onClick={() => moderar(it.id, acao)} className="rounded-lg border px-2 py-1 text-[11px] disabled:opacity-50" data-testid={`sales-action-${acao}-${it.id}`}>
                    {label}
                  </button>
                ))}
                {it.tipo === 'venda' && (
                  <button disabled={busy === it.id} onClick={() => moderar(it.id, 'classificar')} className="rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800 disabled:opacity-50" data-testid={`sales-action-classificar-${it.id}`}>
                    Editar classificação
                  </button>
                )}
                {busy === it.id && <span className="text-[11px] text-slate-500">a atualizar…</span>}
              </div>

              {pending?.id === it.id && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid={`sales-pending-${pending.acao}-${it.id}`}>
                  {pending.acao === 'classificar' ? (
                    <select value={pendingValue} onChange={(e) => setPendingValue(e.target.value)} className="rounded-lg border px-2 py-1 text-[11px]" data-testid="sales-pending-select">
                      <option value="">Escolher categoria…</option>
                      <option value="ex_tvde">Ex-TVDE</option>
                      <option value="oportunidade">Oportunidade para TVDE</option>
                    </select>
                  ) : (
                    <input
                      value={pendingValue}
                      onChange={(e) => setPendingValue(e.target.value)}
                      placeholder={pending.acao === 'rejeitar' ? 'Motivo da rejeição' : 'O que deve ser corrigido'}
                      className="min-w-[220px] flex-1 rounded-lg border px-2 py-1 text-[11px]"
                      data-testid="sales-pending-input"
                    />
                  )}
                  <button disabled={!pendingValue} onClick={() => moderar(it.id, pending.acao, pendingValue)} className="rounded-lg bg-slate-900 px-2 py-1 text-[11px] font-bold text-white disabled:opacity-50" data-testid="sales-pending-confirm">Confirmar</button>
                  <button onClick={() => setPending(null)} className="rounded-lg border px-2 py-1 text-[11px]" data-testid="sales-pending-cancel">Cancelar</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-slate-400">
        Não existe validação documental: o Marketplace não recolhe DUA, VIN, matrícula completa, morada ou documentos.
      </p>
    </section>
  );
}
